/*
  "What is blocking this project?" (UI overhaul wave 3, screen 04 idea 9). Pure helpers, no React.

  buildBlockers(input) → up to `limit` points, most urgent first, each { key, tone, parts: [{ text, strong }] }
  so the card can bold the numbers. Computed from data the page already has (stages, their items and clocks,
  execution, handover date, invoices) — it works with the AI switched off; with AI on, the card can show the
  model's wording of the same facts instead (GET /projects/{id}/blockers/ai).
*/
import { formatDate, formatNumber } from '@/lib/format';
import { isStageDone } from './projectDetailModel';
import { summarizeStageItems } from './stagesModel';

const text = (value) => String(value ?? '').trim();
const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;
const S = (value) => ({ text: String(value), strong: true });
const T = (value) => ({ text: String(value) });
const money = (value) => formatNumber(value, { decimals: 2 });

const listNames = (names = []) => {
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

/**
 * input = {
 *   project, stages (the stages this person sees, in order), linesByStage, clocks ({ [stageId]: stageClock }),
 *   waitingOn (collectWaitingOn), execution (summarizeExecution), handover (getHandoverState),
 *   readyToBill (findReadyToBill — only for roles with Invoicing), limit = 3
 * }
 */
export function buildBlockers({
  project,
  stages = [],
  linesByStage = {},
  clocks = {},
  waitingOn,
  execution,
  handover,
  readyToBill = [],
  limit = 3,
} = {}) {
  const points = [];
  const total = stages.length;
  const approved = stages.filter((stage) => isStageDone(stage.status)).length;
  const open = stages.filter((stage) => !isStageDone(stage.status));

  // 1. The handover date
  if (handover?.overdue && !handover.onHold) {
    points.push({
      key: 'handover-overdue',
      tone: 'red',
      parts: [T('Handover date '), S(formatDate(handover.endDate)), T(' passed '), S(`${plural(handover.days, 'day')} ago`),
        T(' — update the end date or put the project On Hold.')],
    });
  } else if (handover && !handover.closed && !handover.onHold && handover.days <= 0 && handover.days >= -14 && open.length) {
    points.push({
      key: 'handover-soon',
      tone: 'amber',
      parts: [T('Handover is '), S(handover.days === 0 ? 'today' : `in ${plural(-handover.days, 'day')}`), T(` (${formatDate(handover.endDate)}) and `),
        S(`${open.length} of ${total} stages`), T(' are still open.')],
    });
  }

  // 2. Stages over their SLA (04 #8)
  const late = stages
    .map((stage) => ({ stage, clock: clocks[stage.id] }))
    .filter((entry) => entry.clock?.overdue)
    .sort((a, b) => (b.clock.days - b.clock.slaDays) - (a.clock.days - a.clock.slaDays));
  if (late.length) {
    const [{ stage, clock }] = late;
    const what = clock.kind === 'waiting' ? 'has been waiting to start for ' : `has been ${clock.status} for `;
    points.push({
      key: 'sla',
      tone: 'red',
      parts: [S(stage.name), T(` ${what}`), S(plural(clock.days, 'day')), T(` — over its ${clock.slaDays}-day SLA`),
        T(late.length > 1 ? ` (${plural(late.length - 1, 'more stage')} too).` : '.')],
    });
  }

  // 3. Execution ahead of the stage approvals
  if (execution && execution.counted > 0 && total > 0) {
    const execRatio = execution.done / execution.counted;
    if (execRatio >= 0.75 && approved / total < 0.5) {
      const empty = open.filter((stage) => !summarizeStageItems(linesByStage[stage.id] || stage.lines || []).count).slice(0, 2).map((stage) => stage.name);
      const tail = empty.length
        ? [T(` — ${listNames(empty)} ${empty.length === 1 ? 'has' : 'have'} no items or dates.`)]
        : [T(` — ${listNames(open.slice(0, 2).map((stage) => stage.name))} ${open.length === 1 ? 'is' : 'are'} still open.`)];
      points.push({
        key: 'execution-ahead',
        tone: 'amber',
        parts: [T('Execution is '), S(`${execution.done} of ${execution.counted} done`), T(', but only '), S(`${approved} of ${total} stages`),
          T(' are approved'), ...tail],
      });
    }
  }

  // 4. Items waiting on someone
  const parties = (waitingOn?.parties || []).filter((party) => party.items.length);
  if (parties.length) {
    const [party] = [...parties].sort((a, b) => b.items.length - a.items.length);
    const [item] = party.items;
    const parts = party.items.length === 1
      ? [T('The '), S(item.label === item.stageName ? item.stageName : `${item.stageName} · ${item.label}`), T(' item is still '), S(`Pending from ${party.label.toLowerCase()}`), T('.')]
      : [S(plural(party.items.length, 'item')), T(' are '), S(`Pending from ${party.label.toLowerCase()}`),
        T(` (${listNames([...new Set(party.items.map((entry) => entry.stageName))].slice(0, 3))}).`)];
    const others = parties.filter((entry) => entry !== party);
    if (others.length) parts.push(T(` Also waiting on ${listNames(others.map((entry) => entry.label))}.`));
    points.push({ key: 'waiting', tone: 'amber', parts });
  }

  // 5. Work done but not invoiced (07 #7)
  if (readyToBill.length) {
    const [first] = readyToBill;
    const evidence = first.evidence?.total ? `${first.evidence.title} is ${first.evidence.done}/${first.evidence.total} done` : `${first.evidence?.title || 'Its work'} is done`;
    points.push({
      key: 'ready-to-bill',
      tone: 'lime',
      parts: [S(`${first.types.join(', ')} ${money(first.amount)}`), T(' can be invoiced — '), T(evidence),
        T(readyToBill.length > 1 ? ` (${plural(readyToBill.length - 1, 'more payment')} ready too).` : '.')],
    });
  }

  if (!points.length) {
    points.push({
      key: 'clear',
      tone: 'green',
      parts: [T(text(project?.name) ? `Nothing is blocking ${text(project.name)} right now` : 'Nothing is blocking this project right now'),
        T(open.length ? ` — next: ${open[0].name}.` : ' — every stage is approved.')],
    });
  }
  return points.slice(0, limit);
}

/** Plain text of a point (screen readers, copying, tests). */
export const blockerText = (point) => (point?.parts || []).map((part) => part.text).join('');
